import { Alert, Button, Card, Col, Input, Row, Space, Statistic, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { Pagination as ZhPagination, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import Permission from "@/views/components/Permission";

export interface CustomerProfileBasic {
    userName?: string;
    company?: string;
    identityTags?: string[];
    age?: number | string;
    gender?: number | string;
    cityCode?: string;
    commonProvince?: string;
    commonCity?: string;
}

export interface CustomerProfileOverview {
    orderTradeAmount?: number | string;
    settlementAmount?: number | string;
    refundAmount?: number | string;
    orderTon?: number | string;
    actualSettlementTon?: number | string;
    totalOrders?: number | string;
    completedOrders?: number | string;
}

export interface CustomerProfileStats {
    footprintCount?: number;
    collectCount?: number;
    followSupplierCount?: number;
}

export interface CustomerProfileFrequencyItem {
    dateStr?: string;
    orderTradeAmount?: number;
    settlementAmount?: number;
    refundAmount?: number;
    orderTon?: number;
    actualSettlementTon?: number;
    totalOrders?: number;
}

export interface CustomerProfileFrequency {
    frequencyList?: CustomerProfileFrequencyItem[];
}

export interface CustomerProfilePurchaseCategory {
    productTypeName?: string;
    orderCount?: number;
    orderTon?: number;
}

export interface CustomerProfileBehavior {
    moduleName?: string;
    stayTime?: number;
}

export interface CustomerProfileInterestSand {
    productTypeName?: string;
    browseCount?: number;
    stayTime?: number;
}

export interface CustomerProfileGoodsItem {
    productId?: string;
    productType?: number;
    mainFile?: { path?: string };
    title?: string;
    category?: string;
    supplierName?: string;
    goodsCommissioner?: string;
    goodsState?: number | string;
    publishState?: number | string;
    browseTime?: string;
    collectTime?: string;
}

export interface CustomerProfileSupplierItem {
    supplierId?: number;
    supplierName?: string;
    supplierType?: number | string;
    groupName?: string;
    legalPerson?: string;
    address?: string;
    followTime?: string;
}

export interface CustomerProfilePageResult<T> {
    list?: T[];
    total?: number;
}

export interface CustomerProfileBehaviorLog {
    time?: string;
    clientType?: string;
    module?: string;
    eventName?: string;
    stayDuration?: number;
    type?: string | number;
    detailContent?: string | Record<string, unknown>;
}

export interface CustomerProfileBehaviorTotals {
    totalStayDurationMs?: number;
    totalVisitCount?: number;
}

export interface CustomerProfileTimeRange {
    beginTime: string;
    endTime: string;
}

interface CustomerProfileBehaviorFilters {
    keyword?: string;
    clientType?: string;
    functionType?: string;
    type?: string;
    beginTime?: string;
    endTime?: string;
}

type CustomerProfileBehaviorQuery = { customerId: number } & CustomerProfileBehaviorFilters;

export interface CustomerProfilePageProps {
    customerId: number;
    loadTimeRange?: (params: { customerId: number }) => Promise<CustomerProfileTimeRange>;
    loadBasic?: (params: { customerId: number }) => Promise<CustomerProfileBasic>;
    loadOverview?: (params: {
        customerId: number;
        beginTime: string;
        endTime: string;
    }) => Promise<CustomerProfileOverview>;
    loadStats?: (params: { customerId: number; beginTime: string; endTime: string }) => Promise<CustomerProfileStats>;
    loadFrequency?: (params: {
        customerId: number;
        beginTime: string;
        endTime: string;
        timeGranularity: number;
    }) => Promise<CustomerProfileFrequency>;
    loadPurchaseCategory?: (params: {
        customerId: number;
        beginTime: string;
        endTime: string;
        sortType: number;
    }) => Promise<CustomerProfilePurchaseCategory[]>;
    loadBehavior?: (params: {
        customerId: number;
        beginTime: string;
        endTime: string;
    }) => Promise<CustomerProfileBehavior[]>;
    loadInterestSandTop?: (params: {
        customerId: number;
        beginTime: string;
        endTime: string;
        sortType: number;
    }) => Promise<CustomerProfileInterestSand[]>;
    loadFootprint?: (params: {
        customerId: number;
        page: number;
        size: number;
        beginTime: string;
        endTime: string;
    }) => Promise<CustomerProfilePageResult<CustomerProfileGoodsItem>>;
    loadCollectedGoods?: (params: {
        customerId: number;
        page: number;
        size: number;
        beginTime: string;
        endTime: string;
    }) => Promise<CustomerProfilePageResult<CustomerProfileGoodsItem>>;
    loadFollowedSupplier?: (params: {
        customerId: number;
        page: number;
        size: number;
        beginTime: string;
        endTime: string;
    }) => Promise<CustomerProfilePageResult<CustomerProfileSupplierItem>>;
    loadBehaviorLogs?: (params: {
        customerId: number;
        page: number;
        size: number;
        keyword?: string;
        clientType?: string;
        functionType?: string;
        type?: string;
        beginTime?: string;
        endTime?: string;
    }) => Promise<CustomerProfilePageResult<CustomerProfileBehaviorLog>>;
    loadBehaviorTotals?: (params: CustomerProfileBehaviorQuery) => Promise<CustomerProfileBehaviorTotals>;
    loadBehaviorExport?: (params: {
        customerId: number;
        keyword?: string;
        clientType?: string;
        functionType?: string;
        type?: string;
        beginTime?: string;
        endTime?: string;
    }) => Promise<unknown>;
}

const defaultProfileTimeRange: CustomerProfileTimeRange = {
    beginTime: "1970-01-01 00:00:00",
    endTime: "2099-12-31 23:59:59"
};
const genderLabels: Record<number, string> = { 0: "男", 1: "女" };

/** 复用 Vue 用户画像基础信息接口。 */
export async function loadCustomerProfileBasic(params: { customerId: number }) {
    const response = await request.service.get("/admin/user-profile/basic", { params });
    return response.data as CustomerProfileBasic;
}

/** 复用 Vue 用户画像全部时间范围接口。 */
export async function loadCustomerProfileTimeRange(params: { customerId: number }) {
    const response = await request.service.get("/admin/user-profile/time-range", { params });
    return response.data as CustomerProfileTimeRange;
}

/** 复用 Vue 用户画像交易概览接口。 */
export async function loadCustomerProfileOverview(params: { customerId: number; beginTime: string; endTime: string }) {
    const response = await request.service.get("/admin/user-profile/trade-overview", { params });
    return response.data as CustomerProfileOverview;
}

/** 复用 Vue 用户画像关注统计接口。 */
export async function loadCustomerProfileStats(params: { customerId: number; beginTime: string; endTime: string }) {
    const response = await request.service.get("/admin/user-profile/profile/stats", { params });
    return response.data as CustomerProfileStats;
}

/** 复用 Vue 用户画像交易频率接口，默认使用月粒度。 */
export async function loadCustomerProfileFrequency(params: {
    customerId: number;
    beginTime: string;
    endTime: string;
    timeGranularity: number;
}) {
    const response = await request.service.get("/admin/user-profile/trade-frequency", { params });
    return response.data as CustomerProfileFrequency;
}

/** 复用 Vue 用户画像购买品类排行接口。 */
export async function loadCustomerProfilePurchaseCategory(params: {
    customerId: number;
    beginTime: string;
    endTime: string;
    sortType: number;
}) {
    const response = await request.service.get("/admin/user-profile/purchase-category", { params });
    return response.data as CustomerProfilePurchaseCategory[];
}

/** 复用 Vue 用户画像行为分布接口。 */
export async function loadCustomerProfileBehavior(params: { customerId: number; beginTime: string; endTime: string }) {
    const response = await request.service.get("/admin/user-profile/behavior", { params });
    return response.data as CustomerProfileBehavior[];
}

/** 复用 Vue 用户画像感兴趣砂石排行接口。 */
export async function loadCustomerProfileInterestSandTop(params: {
    customerId: number;
    beginTime: string;
    endTime: string;
    sortType: number;
}) {
    const response = await request.service.get("/admin/user-profile/interest-sand-top", { params });
    return response.data as CustomerProfileInterestSand[];
}

/** 复用 Vue 商品足迹分页接口。 */
export async function loadCustomerProfileFootprint(params: {
    customerId: number;
    page: number;
    size: number;
    beginTime: string;
    endTime: string;
}) {
    const response = await request.service.get("/admin/user-profile/goods-footprint/paging", { params });
    return response.data as CustomerProfilePageResult<CustomerProfileGoodsItem>;
}

/** 复用 Vue 收藏商品分页接口。 */
export async function loadCustomerProfileCollectedGoods(params: {
    customerId: number;
    page: number;
    size: number;
    beginTime: string;
    endTime: string;
}) {
    const response = await request.service.get("/admin/user-profile/collected-goods/paging", { params });
    return response.data as CustomerProfilePageResult<CustomerProfileGoodsItem>;
}

/** 复用 Vue 关注供应商分页接口。 */
export async function loadCustomerProfileFollowedSupplier(params: {
    customerId: number;
    page: number;
    size: number;
    beginTime: string;
    endTime: string;
}) {
    const response = await request.service.get("/admin/user-profile/followed-supplier/paging", { params });
    return response.data as CustomerProfilePageResult<CustomerProfileSupplierItem>;
}

/** 复用 Vue 用户画像行为埋点分页接口。 */
export async function loadCustomerProfileBehaviorLogs(params: {
    customerId: number;
    page: number;
    size: number;
    keyword?: string;
    clientType?: string;
    functionType?: string;
    type?: string;
    beginTime?: string;
    endTime?: string;
}) {
    const response = await request.service.get("/admin/user-profile/paging", { params });
    return response.data as CustomerProfilePageResult<CustomerProfileBehaviorLog>;
}

/** 复用 Vue 用户画像行为埋点统计接口，并沿用列表的筛选参数。 */
export async function loadCustomerProfileBehaviorTotals(params: CustomerProfileBehaviorQuery) {
    const response = await request.service.get("/admin/user-profile/total", { params });
    return response.data as CustomerProfileBehaviorTotals;
}

/** 复用 Vue 用户画像行为埋点导出接口并触发浏览器下载。 */
export async function loadCustomerProfileBehaviorExport(params: {
    customerId: number;
    keyword?: string;
    clientType?: string;
    functionType?: string;
    type?: string;
    beginTime?: string;
    endTime?: string;
}) {
    const response = await request.service.get("/admin/user-profile/export-use-profile", {
        params,
        responseType: "blob"
    });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `用户画像${params.customerId}行为埋点.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return blob;
}

const display = (value?: number | string) => (value == null || value === "" ? DEFAULT_VALUE : String(value));

/**
 * Vue 日期组件使用 valueFormat 传递完整时间；使用文本输入承接日期组件契约，
 * 避免浏览器原生 date 输入截断 `YYYY-MM-DD HH:mm:ss`。
 */
function ProfileDateRangePicker({
    value,
    onChange
}: {
    value: [string, string];
    onChange: (_value: unknown, formattedValue: [string, string] | null) => void;
}) {
    return (
        <div
            data-format="YYYY-MM-DD"
            data-range-separator="-"
            data-testid="zhihao-date-range-picker"
            data-type="daterange"
            data-value-format="YYYY-MM-DD HH:mm:ss"
        >
            <Input
                aria-label="画像开始时间"
                value={value[0]}
                onChange={event => onChange([event.target.value, value[1]], [event.target.value, value[1]])}
            />
            <Input
                aria-label="画像结束时间"
                value={value[1]}
                onChange={event => onChange([value[0], event.target.value], [value[0], event.target.value])}
            />
        </div>
    );
}

export function CustomerProfilePage({
    customerId,
    loadTimeRange = loadCustomerProfileTimeRange,
    loadBasic = loadCustomerProfileBasic,
    loadOverview = loadCustomerProfileOverview,
    loadStats = loadCustomerProfileStats,
    loadFrequency = loadCustomerProfileFrequency,
    loadPurchaseCategory = loadCustomerProfilePurchaseCategory,
    loadBehavior = loadCustomerProfileBehavior,
    loadInterestSandTop = loadCustomerProfileInterestSandTop,
    loadFootprint = loadCustomerProfileFootprint,
    loadCollectedGoods = loadCustomerProfileCollectedGoods,
    loadFollowedSupplier = loadCustomerProfileFollowedSupplier,
    loadBehaviorLogs = loadCustomerProfileBehaviorLogs,
    loadBehaviorTotals = loadCustomerProfileBehaviorTotals,
    loadBehaviorExport = loadCustomerProfileBehaviorExport
}: CustomerProfilePageProps) {
    const [profileRange, setProfileRange] = useState<CustomerProfileTimeRange>(defaultProfileTimeRange);
    const [rangeDraft, setRangeDraft] = useState<CustomerProfileTimeRange>(defaultProfileTimeRange);
    const [timeGranularity, setTimeGranularity] = useState(3);
    const [basic, setBasic] = useState<CustomerProfileBasic>();
    const [overview, setOverview] = useState<CustomerProfileOverview>();
    const [stats, setStats] = useState<CustomerProfileStats>();
    const [frequency, setFrequency] = useState<CustomerProfileFrequencyItem[]>([]);
    const [purchaseCategory, setPurchaseCategory] = useState<CustomerProfilePurchaseCategory[]>([]);
    const [behavior, setBehavior] = useState<CustomerProfileBehavior[]>([]);
    const [interestSandTop, setInterestSandTop] = useState<CustomerProfileInterestSand[]>([]);
    const [attentionTab, setAttentionTab] = useState<"footprint" | "collected" | "supplier">("footprint");
    const [attentionPage, setAttentionPage] = useState(1);
    const [attentionTotal, setAttentionTotal] = useState(0);
    const [footprint, setFootprint] = useState<CustomerProfileGoodsItem[]>([]);
    const [collectedGoods, setCollectedGoods] = useState<CustomerProfileGoodsItem[]>([]);
    const [followedSupplier, setFollowedSupplier] = useState<CustomerProfileSupplierItem[]>([]);
    const [behaviorLogs, setBehaviorLogs] = useState<CustomerProfileBehaviorLog[]>([]);
    const [behaviorTotal, setBehaviorTotal] = useState(0);
    const [behaviorTotals, setBehaviorTotals] = useState<CustomerProfileBehaviorTotals>();
    const [behaviorPage, setBehaviorPage] = useState(1);
    const [behaviorDraftFilters, setBehaviorDraftFilters] = useState<CustomerProfileBehaviorFilters>({});
    const [behaviorFilters, setBehaviorFilters] = useState<CustomerProfileBehaviorFilters>({});
    const [behaviorExporting, setBehaviorExporting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    /** 加载 Vue 的全部画像时间范围，并同步当前筛选草稿。 */
    const applyAllProfileTimeRange = async () => {
        try {
            const nextRange = await loadTimeRange({ customerId });
            setProfileRange(nextRange);
            setRangeDraft(nextRange);
            setErrorMessage("");
        } catch {
            setErrorMessage("用户画像时间范围加载失败");
        }
    };

    /** 应用手工输入的画像起止时间。 */
    const applyProfileTimeRange = () => {
        setProfileRange({ ...rangeDraft });
    };

    /** 沿用旧 Vue ZhDatePicker 的日期范围更新格式，保留后端需要的完整时间字符串。 */
    const updateProfileTimeRangeDraft = (_value: unknown, formattedValue: [string, string] | null) => {
        if (!formattedValue) return;
        setRangeDraft({ beginTime: formattedValue[0] || "", endTime: formattedValue[1] || "" });
    };

    useEffect(() => {
        let disposed = false;
        const rangeParams = { customerId, ...profileRange };
        void Promise.all([
            loadBasic({ customerId }),
            loadOverview(rangeParams),
            loadStats(rangeParams),
            loadFrequency({ ...rangeParams, timeGranularity }),
            loadPurchaseCategory({ ...rangeParams, sortType: 2 }),
            loadBehavior(rangeParams),
            loadInterestSandTop({ ...rangeParams, sortType: 2 })
        ])
            .then(
                ([
                    basicResult,
                    overviewResult,
                    statsResult,
                    frequencyResult,
                    purchaseResult,
                    behaviorResult,
                    interestResult
                ]) => {
                    if (disposed) return;
                    setBasic(basicResult);
                    setOverview(overviewResult);
                    setStats(statsResult);
                    setFrequency(frequencyResult.frequencyList || []);
                    setPurchaseCategory(purchaseResult);
                    setBehavior(behaviorResult);
                    setInterestSandTop(interestResult);
                    setErrorMessage("");
                }
            )
            .catch(() => {
                if (!disposed) setErrorMessage("用户画像加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [
        customerId,
        profileRange.beginTime,
        profileRange.endTime,
        timeGranularity,
        loadBasic,
        loadOverview,
        loadStats,
        loadFrequency,
        loadPurchaseCategory,
        loadBehavior,
        loadInterestSandTop
    ]);

    useEffect(() => {
        let disposed = false;
        const params = {
            customerId,
            page: attentionPage,
            size: 20,
            ...profileRange
        };
        const loadAttention =
            attentionTab === "footprint"
                ? loadFootprint(params)
                : attentionTab === "collected"
                  ? loadCollectedGoods(params)
                  : loadFollowedSupplier(params);

        void loadAttention
            .then(result => {
                if (disposed) return;
                setAttentionTotal(result.total || 0);
                if (attentionTab === "footprint") setFootprint(result.list || []);
                if (attentionTab === "collected") setCollectedGoods(result.list || []);
                if (attentionTab === "supplier") setFollowedSupplier(result.list || []);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("用户画像加载失败");
            });

        return () => {
            disposed = true;
        };
    }, [
        customerId,
        attentionPage,
        attentionTab,
        profileRange.beginTime,
        profileRange.endTime,
        loadFootprint,
        loadCollectedGoods,
        loadFollowedSupplier
    ]);

    useEffect(() => {
        let disposed = false;
        const params = { customerId, page: behaviorPage, size: 20, ...behaviorFilters };
        // 旧 Vue 会用当前行为筛选条件重新统计总计，避免列表与统计卡片口径不一致。
        const totalParams = { customerId, ...behaviorFilters };
        void Promise.all([loadBehaviorTotals(totalParams), loadBehaviorLogs(params)])
            .then(([totalResult, pageResult]) => {
                if (disposed) return;
                setBehaviorTotals(totalResult);
                setBehaviorTotal(pageResult.total || 0);
                setBehaviorLogs(pageResult.list || []);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("用户画像加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [customerId, behaviorPage, behaviorFilters, loadBehaviorLogs, loadBehaviorTotals]);

    const switchAttentionTab = (tab: "footprint" | "collected" | "supplier") => {
        setAttentionTab(tab);
        setAttentionPage(1);
    };

    const formatStayDuration = (value?: number) => {
        if (value == null) return DEFAULT_VALUE;
        if (value < 1) return "<1s";
        return `${Math.floor(value / 60)}min${Math.floor(value % 60)}s`;
    };

    const updateBehaviorFilter = (key: keyof CustomerProfileBehaviorFilters, value: string) => {
        setBehaviorDraftFilters(current => ({ ...current, [key]: value }));
    };

    const applyBehaviorFilters = () => {
        const nextFilters = Object.fromEntries(
            Object.entries(behaviorDraftFilters)
                .filter(([, value]) => value != null && value !== "")
                .map(([key, value]) => {
                    if (key === "beginTime" && value && !value.includes(":")) {
                        return [key, `${value} 00:00:00`];
                    }
                    if (key === "endTime" && value && !value.includes(":")) {
                        return [key, `${value} 23:59:59`];
                    }
                    return [key, value];
                })
        ) as CustomerProfileBehaviorFilters;
        setBehaviorFilters(nextFilters);
        setBehaviorPage(1);
    };

    const exportBehaviorLogs = async () => {
        if (behaviorTotal > 10000) {
            setErrorMessage("导出失败，导出内容超过10000条，请筛选后重新导出");
            return;
        }
        setBehaviorExporting(true);
        try {
            await loadBehaviorExport({ customerId, ...behaviorFilters });
        } catch {
            setErrorMessage("导出失败");
        } finally {
            setBehaviorExporting(false);
        }
    };

    // 画像统计表复用 Vue 字段和单位，统一转换为 zhihao-ui-react 列模型。
    const frequencyColumns: TableColumnProps<CustomerProfileFrequencyItem>[] = [
        { label: "时间", prop: "dateStr" },
        { label: "订单交易金额(元)", prop: "orderTradeAmount" },
        { label: "结算金额(元)", prop: "settlementAmount" },
        { label: "退款金额(元)", prop: "refundAmount" },
        { label: "下单吨数(吨)", prop: "orderTon" },
        { label: "实际结算吨数(吨)", prop: "actualSettlementTon" },
        { label: "生成订单(笔)", prop: "totalOrders" }
    ];
    const purchaseCategoryColumns: TableColumnProps<CustomerProfilePurchaseCategory & { rank: number }>[] = [
        { label: "排名", prop: "rank" },
        { label: "品类", prop: "productTypeName" },
        { label: "订单量", prop: "orderCount" },
        { label: "下单吨数(吨)", prop: "orderTon" }
    ];
    const behaviorColumns: TableColumnProps<CustomerProfileBehavior>[] = [
        { label: "模块", prop: "moduleName" },
        { label: "停留时长", prop: "stayTime", render: ({ row }) => `${display(row.stayTime)} 秒` }
    ];
    const interestSandColumns: TableColumnProps<CustomerProfileInterestSand & { rank: number }>[] = [
        { label: "排名", prop: "rank" },
        { label: "品类", prop: "productTypeName" },
        { label: "用户访问次数", prop: "browseCount" },
        { label: "用户停留时长", prop: "stayTime", render: ({ row }) => `${display(row.stayTime)} 秒` }
    ];
    const supplierColumns: TableColumnProps<CustomerProfileSupplierItem>[] = [
        { label: "供应商名称", prop: "supplierName" },
        { label: "供应商类型", prop: "supplierType" },
        { label: "所属集团", prop: "groupName" },
        { label: "法人姓名", prop: "legalPerson" },
        { label: "所在地", prop: "address" },
        { label: "关注时间", prop: "followTime" }
    ];
    const goodsColumns: TableColumnProps<CustomerProfileGoodsItem>[] = [
        { label: "商品编号", prop: "productId" },
        {
            label: "主图/标题",
            prop: "title",
            render: ({ row }) => (
                <Space>
                    {row.mainFile?.path ? (
                        <img alt={row.title || "商品主图"} height={40} src={row.mainFile.path} width={40} />
                    ) : null}
                    <span>{row.title || DEFAULT_VALUE}</span>
                </Space>
            )
        },
        { label: "品类", prop: "category" },
        { label: "供应商", prop: "supplierName" },
        { label: "商品专员", prop: "goodsCommissioner" },
        { label: "商品状态", prop: "goodsState" },
        { label: "发布状态", prop: "publishState" },
        {
            label: attentionTab === "footprint" ? "浏览时间" : "收藏时间",
            prop: attentionTab === "footprint" ? "browseTime" : "collectTime"
        }
    ];
    const behaviorLogColumns: TableColumnProps<CustomerProfileBehaviorLog>[] = [
        { label: "时间", prop: "time" },
        { label: "终端", prop: "clientType" },
        { label: "模块", prop: "module" },
        { label: "事件名称", prop: "eventName" },
        { label: "停留时长", prop: "stayDuration", render: ({ row }) => formatStayDuration(row.stayDuration) },
        { label: "事件类型", prop: "type" },
        {
            label: "详情内容",
            prop: "detailContent",
            render: ({ row }) =>
                row.detailContent
                    ? typeof row.detailContent === "string"
                        ? row.detailContent
                        : JSON.stringify(row.detailContent)
                    : DEFAULT_VALUE
        }
    ];

    return (
        <section aria-label="customer-profile-page" className="flex min-h-full flex-col gap-4">
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            <Card title="画像时间范围">
                <Space wrap>
                    <Button aria-label="全部" onClick={() => void applyAllProfileTimeRange()}>
                        全部
                    </Button>
                    <Button
                        aria-label="日"
                        type={timeGranularity === 1 ? "primary" : "default"}
                        onClick={() => setTimeGranularity(1)}
                    >
                        日
                    </Button>
                    <Button
                        aria-label="月"
                        type={timeGranularity === 3 ? "primary" : "default"}
                        onClick={() => setTimeGranularity(3)}
                    >
                        月
                    </Button>
                    <Button
                        aria-label="年"
                        type={timeGranularity === 4 ? "primary" : "default"}
                        onClick={() => setTimeGranularity(4)}
                    >
                        年
                    </Button>
                    <ProfileDateRangePicker
                        value={[rangeDraft.beginTime, rangeDraft.endTime]}
                        onChange={updateProfileTimeRangeDraft}
                    />
                    <Button aria-label="应用画像时间范围" onClick={applyProfileTimeRange}>
                        应用
                    </Button>
                </Space>
            </Card>
            <Card>
                <Typography.Title level={5}>用户基本信息</Typography.Title>
                <Space align="start" wrap>
                    <div>
                        <Typography.Text strong>{basic?.userName || DEFAULT_VALUE}</Typography.Text>
                        <div>企业：{basic?.company || "暂未认证企业"}</div>
                        <Space wrap>
                            {(basic?.identityTags || []).map(tag => (
                                <Tag key={tag}>{tag}</Tag>
                            ))}
                        </Space>
                    </div>
                    <div>年龄：{display(basic?.age)}</div>
                    <div>性别：{genderLabels[Number(basic?.gender)] || DEFAULT_VALUE}</div>
                    <div>IP：{basic?.cityCode || DEFAULT_VALUE}</div>
                    <div>常用收货地：{basic?.commonProvince || basic?.commonCity || DEFAULT_VALUE}</div>
                </Space>
            </Card>
            <Card>
                <Typography.Title level={5}>交易概览</Typography.Title>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8}>
                        <Statistic title="订单交易金额" value={`${display(overview?.orderTradeAmount)} 元`} />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Statistic title="结算金额" value={`${display(overview?.settlementAmount)} 元`} />
                        <Typography.Text type="secondary">
                            退款金额：{display(overview?.refundAmount)} 元
                        </Typography.Text>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Statistic title="下单吨数" value={`${display(overview?.orderTon)} 吨`} />
                        <Typography.Text type="secondary">
                            实际结算吨数：{display(overview?.actualSettlementTon)} 吨
                        </Typography.Text>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Statistic title="产生订单" value={`${display(overview?.totalOrders)} 笔`} />
                        <Typography.Text type="secondary">
                            已完成订单：{display(overview?.completedOrders)} 笔
                        </Typography.Text>
                    </Col>
                </Row>
            </Card>
            <Card>
                <Typography.Title level={5}>用户关注情况</Typography.Title>
                <Row gutter={24}>
                    <Col>
                        <Statistic title="商品足迹" value={stats?.footprintCount ?? 0} />
                    </Col>
                    <Col>
                        <Statistic title="收藏商品" value={stats?.collectCount ?? 0} />
                    </Col>
                    <Col>
                        <Statistic title="关注供应商数" value={stats?.followSupplierCount ?? 0} />
                    </Col>
                </Row>
            </Card>
            <Row gutter={[16, 16]}>
                <Col xs={24} xl={12}>
                    <Card title="交易频率分析">
                        <ZhTable<CustomerProfileFrequencyItem>
                            rowKey={row => row.dateStr || "frequency"}
                            size="small"
                            pagination={false}
                            data={frequency}
                            emptyText="暂无数据"
                            columns={frequencyColumns}
                        />
                    </Card>
                </Col>
                <Col xs={24} xl={12}>
                    <Card title="购买品类TOP5">
                        <ZhTable<CustomerProfilePurchaseCategory & { rank: number }>
                            rowKey={row => row.productTypeName || "category"}
                            size="small"
                            pagination={false}
                            data={purchaseCategory.map((item, index) => ({ ...item, rank: index + 1 }))}
                            emptyText="暂无数据"
                            columns={purchaseCategoryColumns}
                        />
                    </Card>
                </Col>
                <Col xs={24} xl={12}>
                    <Card title="用户行为分布">
                        <ZhTable<CustomerProfileBehavior>
                            rowKey={row => row.moduleName || "behavior"}
                            size="small"
                            pagination={false}
                            data={behavior}
                            emptyText="暂无数据"
                            columns={behaviorColumns}
                        />
                    </Card>
                </Col>
                <Col xs={24} xl={12}>
                    <Card title="用户感兴趣砂石TOP5">
                        <ZhTable<CustomerProfileInterestSand & { rank: number }>
                            rowKey={row => row.productTypeName || "interest"}
                            size="small"
                            pagination={false}
                            data={interestSandTop.map((item, index) => ({ ...item, rank: index + 1 }))}
                            emptyText="暂无数据"
                            columns={interestSandColumns}
                        />
                    </Card>
                </Col>
            </Row>
            <Card title="用户关注情况">
                <Space className="mb-4" wrap>
                    <button
                        aria-label="商品足迹"
                        className={attentionTab === "footprint" ? "font-semibold text-blue-600" : ""}
                        type="button"
                        onClick={() => switchAttentionTab("footprint")}
                    >
                        商品足迹
                    </button>
                    <button
                        aria-label="收藏商品"
                        className={attentionTab === "collected" ? "font-semibold text-blue-600" : ""}
                        type="button"
                        onClick={() => switchAttentionTab("collected")}
                    >
                        收藏商品
                    </button>
                    <button
                        aria-label="关注供应商"
                        className={attentionTab === "supplier" ? "font-semibold text-blue-600" : ""}
                        type="button"
                        onClick={() => switchAttentionTab("supplier")}
                    >
                        关注供应商
                    </button>
                </Space>
                {attentionTab === "supplier" ? (
                    <>
                        <ZhTable<CustomerProfileSupplierItem>
                            rowKey={row => String(row.supplierId || row.supplierName || "")}
                            data={followedSupplier}
                            emptyText="暂无数据"
                            pagination={false}
                            size="small"
                            columns={supplierColumns}
                            append={
                                <div className="admin-table-composed-pagination">
                                    <ZhPagination
                                        current={attentionPage}
                                        pageSize={20}
                                        total={attentionTotal}
                                        onChange={setAttentionPage}
                                    />
                                </div>
                            }
                        />
                    </>
                ) : (
                    <>
                        <ZhTable<CustomerProfileGoodsItem>
                            rowKey={row => String(row.productId || row.title || "")}
                            data={attentionTab === "footprint" ? footprint : collectedGoods}
                            emptyText="暂无数据"
                            pagination={false}
                            size="small"
                            columns={goodsColumns}
                            append={
                                <div className="admin-table-composed-pagination">
                                    <ZhPagination
                                        current={attentionPage}
                                        pageSize={20}
                                        total={attentionTotal}
                                        onChange={setAttentionPage}
                                    />
                                </div>
                            }
                        />
                    </>
                )}
            </Card>
            <Card title="行为埋点">
                <ZhTable<CustomerProfileBehaviorLog>
                    rowKey={row => `${row.time || "log"}-${row.eventName || "event"}`}
                    size="small"
                    data={behaviorLogs}
                    emptyText="暂无数据"
                    pagination={false}
                    columns={behaviorLogColumns}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Row gutter={24} className="mb-4">
                                <Col>
                                    <Statistic
                                        title="用户停留时长"
                                        value={formatStayDuration(behaviorTotals?.totalStayDurationMs)}
                                    />
                                </Col>
                                <Col>
                                    <Statistic title="用户访问次数" value={behaviorTotals?.totalVisitCount ?? 0} />
                                </Col>
                            </Row>

                            <Space className="mb-4" wrap>
                                <Input
                                    aria-label="事件名称"
                                    placeholder="请输入事件名称"
                                    value={behaviorDraftFilters.keyword}
                                    onChange={event => updateBehaviorFilter("keyword", event.target.value)}
                                />
                                <Input
                                    aria-label="终端"
                                    placeholder="请输入终端"
                                    value={behaviorDraftFilters.clientType}
                                    onChange={event => updateBehaviorFilter("clientType", event.target.value)}
                                />
                                <Input
                                    aria-label="模块"
                                    placeholder="请输入模块"
                                    value={behaviorDraftFilters.functionType}
                                    onChange={event => updateBehaviorFilter("functionType", event.target.value)}
                                />
                                <Input
                                    aria-label="事件类型"
                                    placeholder="请输入事件类型"
                                    value={behaviorDraftFilters.type}
                                    onChange={event => updateBehaviorFilter("type", event.target.value)}
                                />
                                <Input
                                    aria-label="开始时间"
                                    placeholder="开始时间"
                                    value={behaviorDraftFilters.beginTime}
                                    onChange={event => updateBehaviorFilter("beginTime", event.target.value)}
                                />
                                <Input
                                    aria-label="结束时间"
                                    placeholder="结束时间"
                                    value={behaviorDraftFilters.endTime}
                                    onChange={event => updateBehaviorFilter("endTime", event.target.value)}
                                />
                                <Button aria-label="查询行为埋点" type="primary" onClick={applyBehaviorFilters}>
                                    查询行为埋点
                                </Button>
                                <Permission anyOf={["ROLE_SS_CUSTOMER_W"]}>
                                    <Button
                                        aria-label="导出行为埋点"
                                        loading={behaviorExporting}
                                        onClick={() => void exportBehaviorLogs()}
                                    >
                                        导出行为埋点
                                    </Button>
                                </Permission>
                            </Space>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={behaviorPage}
                                pageSize={20}
                                total={behaviorTotal}
                                onChange={setBehaviorPage}
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default CustomerProfilePage;
